import React, { forwardRef } from 'react';
import Type from 'prop-types';
import { useToggle } from 'react-use';
import {
  Input,
  InputAdornment,
  InputLabel,
  IconButton,
  FormControl,
  FormHelperText,
  FilledInput,
  OutlinedInput,
} from '@mui/material';
import {
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
} from '@mui/icons-material';
import { fieldToTextField } from 'formik-mui';
import { useTranslation } from 'hooks';

const variantComponent = {
  standard: Input,
  filled: FilledInput,
  outlined: OutlinedInput,
};

const PasswordField = forwardRef(function PasswordField(props, ref) {
  const {
    autoComplete,
    autoFocus,
    color,
    defaultValue,
    disabled,
    error,
    FormHelperTextProps,
    fullWidth,
    helperText,
    hiddenLabel,
    id,
    inputProps,
    inputRef,
    label,
    name,
    onBlur,
    onChange,
    onFocus,
    placeholder,
    required,
    value,
    variant,
    ...other
  } = fieldToTextField(props);
  const [visible, toggleVisibility] = useToggle(false);
  const { t } = useTranslation();
  const helperTextId = helperText && id ? `${id}-helper-text` : undefined;
  const inputLabelId = label && id ? `${id}-label` : undefined;
  const InputComponent = variantComponent[variant] ?? Input;

  const handleMouseDown = (event) => {
    event.preventDefault();
  };

  const endAdornment = (
    <InputAdornment position="end">
      <IconButton
        aria-label={t('togglePasswordVisibility')}
        onMouseDown={handleMouseDown}
        onMouseUp={handleMouseDown}
        onClick={toggleVisibility}
        edge="end"
      >
        {visible ? <VisibilityIcon /> : <VisibilityOffIcon />}
      </IconButton>
    </InputAdornment>
  );

  return (
    <FormControl
      color={color}
      disabled={disabled}
      error={error}
      fullWidth={fullWidth}
      hiddenLabel={hiddenLabel}
      ref={ref}
      required={required}
      variant={variant}
      {...other}
    >
      {label && (
        <InputLabel htmlFor={id} id={inputLabelId}>
          {label}
        </InputLabel>
      )}
      <InputComponent
        aria-describedby={helperTextId}
        autoComplete={autoComplete}
        autoFocus={autoFocus}
        defaultValue={defaultValue}
        fullWidth={fullWidth}
        id={id}
        inputRef={inputRef}
        inputProps={inputProps}
        name={name}
        onBlur={onBlur}
        onChange={onChange}
        onFocus={onFocus}
        placeholder={placeholder}
        type={visible ? 'text' : 'password'}
        value={value}
        endAdornment={endAdornment}
      />
      {helperText && (
        <FormHelperText id={helperTextId} {...FormHelperTextProps}>
          {helperText}
        </FormHelperText>
      )}
    </FormControl>
  );
});

PasswordField.propTypes = {
  variant: Type.oneOf(['filled', 'outlined', 'standard']),
  disabled: Type.bool,
  error: Type.bool,
  required: Type.bool,
  fullWidth: Type.bool,
  color: Type.oneOf(['primary', 'secondary']),
};

PasswordField.defaultProps = {
  variant: 'standard',
  color: 'primary',
  disabled: false,
  error: false,
  required: false,
  fullWidth: false,
};

export default PasswordField;
